import {
  type CaraWorkspaceFieldsFragment,
  CaraWorkspacePlanStageEventType,
  CaraWorkspaceTool,
  type CustomFormListItem
} from '@oysterjs/ais/api/graphql';

import { renderAddress, titleCase, titleCaseFromCamelCase } from './utils';

type StageEvent = CaraWorkspaceFieldsFragment['planStages'][0]['events'][0];
type SearchFileResult = NonNullable<
  NonNullable<StageEvent['toolCall']>['searchFileTool']
>['output']['results'][number];

/**
 * One section per question. A question asked of several files comes back as
 * consecutive results, one per file, so they sit under the one question, each
 * headed by the file it answered for. A result without a file name, such as one
 * recorded before answers carried one, keeps a section of its own.
 */
const searchFileContent = (results: SearchFileResult[]): string => {
  const sections: { query: string; results: SearchFileResult[] }[] = [];
  for (const result of results) {
    const last = sections[sections.length - 1];
    if (result.fileName && last?.results[0].fileName && last.query === result.query) {
      last.results.push(result);
    } else {
      sections.push({ query: result.query, results: [result] });
    }
  }
  return sections
    .map(({ query, results: answers }, i) =>
      [
        `# ${i + 1}. ${query}`,
        ...answers.map((r) => (r.fileName ? `## ${r.fileName}\n\n${r.answer}` : r.answer))
      ].join('\n\n')
    )
    .join('\n\n');
};

/**
 * AMS write-tool descriptor used to extract human-readable content from any of
 * the per-AMS write tool calls (AgencyZoom, HawkSoft, NowCerts, EZLynx, AMS360, QQCatalyst).
 * Mirrors the table in `conversation-stage-event-renderers.tsx` to keep behavior
 * in sync without making the renderer file the source of truth for plain-text
 * extraction.
 */
const amsToolFor = (event: StageEvent) => {
  const toolCall = event.toolCall;
  if (!toolCall) return undefined;
  switch (toolCall.type) {
    case CaraWorkspaceTool.AgencyzoomWrite:
      return toolCall.agencyzoomWriteTool;
    case CaraWorkspaceTool.HawksoftWrite:
      return toolCall.hawksoftWriteTool;
    case CaraWorkspaceTool.NowcertsWrite:
      return toolCall.nowcertsWriteTool;
    case CaraWorkspaceTool.EzlynxWrite:
      return toolCall.ezlynxWriteTool;
    case CaraWorkspaceTool.Ams360Write:
      return toolCall.ams360WriteTool;
    case CaraWorkspaceTool.QqcatalystWrite:
      return toolCall.qqcatalystWriteTool;
    default:
      return undefined;
  }
};

const writeIntegrationContent = (event: StageEvent): string => {
  const tool = event.toolCall?.writeIntegrationTool;
  if (event.error) return event.error;
  if (tool?.output?.success === false) return tool.output.message ?? '';
  if (!tool?.input?.action) return '';
  return [
    { key: 'Action', value: titleCase(tool.input.action) },
    ...Object.entries(tool.input.account).map(([key, value]) =>
      key === 'address' && typeof value === 'object'
        ? { key: 'Address', value: renderAddress(value) }
        : { key: titleCaseFromCamelCase(key), value }
    ),
    ...Object.entries(tool.input.uploadDocument || {}).map(([key, value]) => ({
      key: titleCaseFromCamelCase(key),
      value
    })),
    ...Object.entries(tool.input.createNote || {}).map(([key, value]) => ({
      key: titleCaseFromCamelCase(key),
      value
    })),
    ...Object.entries(tool.input.createTask || {}).map(([key, value]) => ({
      key: titleCaseFromCamelCase(key),
      value
    }))
  ]
    .filter((p) => !!p.value && !p.key.startsWith('__'))
    .map((p) => `**${p.key}**: ${p.value}`)
    .join('\n\n');
};

const amsWriteContent = (event: StageEvent): string => {
  const tool = amsToolFor(event);
  if (event.error) return typeof event.error === 'string' ? event.error : '';
  if (tool?.output?.success === false) return tool.output.message ?? '';
  if (!tool?.input) return '';
  return [
    typeof tool.input.operation === 'string'
      ? { key: 'Operation', value: titleCase(tool.input.operation) }
      : null,
    ...Object.entries(tool.input)
      .filter(([key]) => key !== 'operation' && key !== '__typename')
      .flatMap(([, value]) => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
        return Object.entries(value as Record<string, unknown>)
          .filter(([k]) => !k.startsWith('__'))
          .map(([k, v]) => ({ key: titleCaseFromCamelCase(k), value: v }));
      })
  ]
    .filter(
      (p): p is { key: string; value: unknown } => !!p && !!p.value && String(p.value).length > 0
    )
    .map((p) => `**${p.key}**: ${p.value}`)
    .join('\n\n');
};

const pdfEditorContent = (event: StageEvent): string => {
  const op = event.toolCall?.pdfEditorTool?.input?.operation;
  const out = event.toolCall?.pdfEditorTool?.output;

  if (op === 'LIST') {
    const forms = out?.forms ?? [];
    if (forms.length) return forms.map((f) => `- **${f.name}**: ${f.description}`).join('\n');
    return event.endedAt
      ? 'No custom forms are available.'
      : 'Looking up available custom forms...';
  }

  if (op === 'DESCRIBE' || op === 'INSPECT_FORM_FIELDS') {
    if (event.error) return event.error;
    if (!event.endedAt) return 'Fetching form schema and fill instructions...';
    if (out?.result) return '```\n' + out.result + '\n```';
    const form = out?.form as CustomFormListItem | undefined;
    if (!form) return 'Retrieved form schema and fill instructions.';
    const formName = form.name ?? event.toolCall?.pdfEditorTool?.input?.formId;
    const chunks = [
      formName && `**Form:** ${formName}`,
      form.description && `_${form.description}_`,
      form.instructions && `**Fill instructions**\n\n${form.instructions}`,
      form.schema != null &&
        `**Field schema (JSON)**\n\n\`\`\`json\n${JSON.stringify(form.schema, null, 2)}\n\`\`\``
    ].filter((x): x is string => Boolean(x));
    return chunks.join('\n\n') || 'Retrieved form schema and fill instructions.';
  }

  return '';
};

/**
 * Returns the markdown body shown when a stage event row is expanded, mirroring
 * the per-tool string-construction logic of `conversation-stage-event-renderers.tsx`.
 * Returns the empty string for events that are not row-style or have no body.
 *
 * Keep this in sync with the renderer file.
 */
export const getStepContent = (event: StageEvent): string => {
  if (event.type === CaraWorkspacePlanStageEventType.Thought) return event.content || '';

  switch (event.toolCall?.type) {
    case CaraWorkspaceTool.SearchFile: {
      const tool = event.toolCall.searchFileTool;
      if (tool?.output?.results?.length) return searchFileContent(tool.output.results);
      if (tool?.input?.queries?.length) {
        return tool.input.queries.map((q, i) => `# ${i + 1}. ${q.query}`).join('\n\n');
      }
      return '';
    }

    case CaraWorkspaceTool.SearchKnowledgeBase: {
      const tool = event.toolCall.searchKnowledgeBaseTool;
      const userQuery = tool?.input?.userQuery || '';
      const answer = tool?.output?.answer || '';
      if (!userQuery && !answer) return '';
      return `### ${userQuery}\n\n${answer}`;
    }

    case CaraWorkspaceTool.SearchKnowledgeGraph: {
      const tool = event.toolCall.searchKnowledgeGraphTool;
      const entities = tool?.output?.entities;
      if (entities?.length) {
        return `Found ${entities.length} entit${entities.length === 1 ? 'y' : 'ies'}`;
      }
      if (event.endedAt) return 'No relevant results found in the knowledge graph';
      if (tool?.input?.queries?.length) {
        return tool.input.queries.map((q, i) => `# ${i + 1}. ${q.query}`).join('\n\n');
      }
      return '';
    }

    case CaraWorkspaceTool.SearchWeb: {
      const tool = event.toolCall.searchWebTool;
      if (tool?.output?.results?.length) {
        return tool.output.results
          .map((r, i) => `# ${i + 1}. ${r.query}\n\n${r.answer}`)
          .join('\n\n');
      }
      if (tool?.input?.queries?.length) {
        return tool.input.queries.map((q, i) => `# ${i + 1}. ${q.query}`).join('\n\n');
      }
      return '';
    }

    case CaraWorkspaceTool.WriteIntegration:
      return writeIntegrationContent(event);

    case CaraWorkspaceTool.PdfEditor:
      return pdfEditorContent(event);

    case CaraWorkspaceTool.AgencyzoomWrite:
    case CaraWorkspaceTool.HawksoftWrite:
    case CaraWorkspaceTool.NowcertsWrite:
    case CaraWorkspaceTool.EzlynxWrite:
    case CaraWorkspaceTool.Ams360Write:
    case CaraWorkspaceTool.QqcatalystWrite:
      return amsWriteContent(event);

    case CaraWorkspaceTool.CodeExecution: {
      if (event.error) return typeof event.error === 'string' ? event.error : '';
      const script = event.toolCall?.codeExecutionTool?.input?.pythonScript?.trim();
      if (!script) return '';
      return '```python\n' + script + '\n```';
    }

    default:
      return '';
  }
};
